import {useSelector} from 'react-redux'
import {getCurrencyCode} from 'modules/meta/selectors'
import {getHotelOfferEntityOffers} from 'modules/sapiSearch/selectors'
import {getIsPrimaryOfferSplitBookingForPriceInsights} from 'modules/searchApi/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {RootState} from 'store'

import {isInsightsDataValid} from '../utils/validation'
import {usePriceTrendInsights} from './usePriceTrendInsights'

interface UseArePriceInsightsAvailableParams {
  hotelId?: string
}

interface UseArePriceInsightsAvailableReturn {
  /** Whether price insights are enabled for this user (API returned valid data and hotel has offers) */
  isEnabled: boolean
  /**
   * Insights payload validity alone (7+ days of data, positive median),
   * ignoring offers and split-booking. Lets callers distinguish "no data for
   * this hotel" from "offers not loaded yet" while the page is still settling.
   */
  hasValidInsights: boolean
  /** Whether the API is still loading */
  isLoading: boolean
  /** Whether there was an API error */
  isError: boolean
}

/**
 * Hook that determines whether Price Insights (with similar hotels) should be shown.
 *
 * Price Insights are shown only when the API returns valid data with sufficient
 * historical days, the hotel has offers (not sold out), and the primary offer
 * is NOT a split booking. Split-booking primaries are excluded — the SB
 * rooms-switch flow has different pricing semantics that don't compare cleanly
 * against a per-night cohort.
 *
 * Consumed by both mobile (`PriceTrendMobileEntry`) and desktop. Keeping the SB
 * check here means the gate fires on first render — no flicker between an
 * initial paint and a later SB-detected hide.
 *
 * Note: Filter-based hiding logic (isPrimaryOfferCheapest) is handled inside
 * the PriceTrend components themselves, not in this hook.
 *
 * @param params - Configuration with hotelId
 * @returns Enabled state and loading/error flags
 */
export const useArePriceInsightsAvailable = ({
  hotelId
}: UseArePriceInsightsAvailableParams = {}): UseArePriceInsightsAvailableReturn => {
  const {checkIn, checkOut, rooms} = useSelector(getSearchBoxValues)
  const currency = useSelector(getCurrencyCode)

  // Check if hotel has offers (not sold out)
  const offers = useSelector((state: RootState) =>
    hotelId ? getHotelOfferEntityOffers(state, hotelId) : undefined
  )
  const hasOffers = offers && offers.length > 0

  // Exclude SB-primary hotels — covers IsYourChoice, TheCheapest, and the
  // 10%-promotion paths via `hasPrimarySplitBookingOffer`, with the rooms
  // signal as a residual catch.
  const isPrimaryOfferSplitBooking = useSelector(
    getIsPrimaryOfferSplitBookingForPriceInsights
  )

  const shouldFetchInsights = Boolean(hotelId)
  const {insights, isLoading, isError} = usePriceTrendInsights(
    shouldFetchInsights
      ? {hotelId, checkIn, checkOut, rooms, currency}
      : undefined
  )

  const hasValidInsights = isInsightsDataValid(insights)
  const isEnabled = Boolean(
    hasValidInsights && hasOffers && !isPrimaryOfferSplitBooking
  )

  return {
    isEnabled,
    hasValidInsights,
    isLoading,
    isError
  }
}
